// components/daypass/dayPassFormModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Button,
    Input,
    Select,
    Option,
    Textarea,
    Typography,
    Checkbox
} from "@material-tailwind/react";
import { useCreateDayPass, useUpdateDayPass } from "../../../custom-hooks/use-daypasses";
import { convertToPresentableText } from "../../../utils/functions";
import { ModalX } from '../../../components';
import { DAY_PASSES_TYPES_ENUM, HOURLY_TIME_SLOTS_ENUM } from '../../../utils/enums';
import { DAY_PASSES_TYPES_DESCRIPTION } from '../../../utils/enums_descriptions';

const DayPassFormModal = ({
    isOpen,
    onClose,
    defaultData,
    customerId,
    onSuccess
}) => {
    const [formData, setFormData] = useState(defaultData);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createDayPassMutation = useCreateDayPass();
    const updateDayPassMutation = useUpdateDayPass();
    const isEditMode = !!formData._id;

    useEffect(() => {
        if (isOpen) {
            setFormData(defaultData);
        }
    }, [isOpen, defaultData]);

    console.log('formData', formData);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Basic validation
            // if (!formData.customerName || !formData.customerEmail || !formData.customerPhone ||
            //     !formData.visitDate || !formData.startTime || !formData.endTime) {
            //     Notify('Please fill in all required fields', 'error');
            //     return;
            // }

            // if (!formData.seatId && !formData.officeId) {
            //     Notify('Please specify either a seat ID or office ID', 'error');
            //     return;
            // }

            setIsSubmitting(true);

            // Format data for API
            const apiData = {
                ...formData,
                userId: customerId,
                visitDate: new Date(formData.visitDate).toISOString(),
                startTime: formData.startTime ? new Date(`${formData.visitDate}T${formData.startTime}`).toISOString() : null,
                endTime: formData.endTime ? new Date(`${formData.visitDate}T${formData.endTime}`).toISOString() : null,
            };

            console.log('apiData', apiData);

            if (isEditMode) {
                await updateDayPassMutation.mutateAsync({ dayPassId: formData._id, data: apiData });
            } else {
                await createDayPassMutation.mutateAsync(apiData);
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving day pass:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEquipmentChange = (equipment, checked) => {
        setFormData(prev => ({
            ...prev,
            equipmentRequested: checked
                ? [...prev.equipmentRequested, equipment]
                : prev.equipmentRequested.filter(item => item !== equipment)
        }));
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title="Create Day Pass" maxWidth="xl">
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Customer Information */}
                <div className="space-y-4">
                    <Typography variant="h6" color="blue-gray">Customer Information</Typography>

                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                        <Input
                            label="Customer Name *"
                            value={formData.customerName}
                            onChange={(e) => handleInputChange('customerName', e.target.value)}
                        />
                        <Input
                            label="Customer Email *"
                            type="email"
                            value={formData.customerEmail}
                            onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                        />
                    </div>

                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                        <Input
                            label="Customer Phone *"
                            value={formData.customerPhone}
                            onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                        />
                        <Input
                            label="CNIC (Optional)"
                            value={formData.customerCNIC}
                            onChange={(e) => handleInputChange('customerCNIC', e.target.value)}
                        />
                    </div>
                </div>

                {/* Pass Details */}
                <div className="space-y-4">
                    <Typography variant="h6" color="blue-gray">Pass Details</Typography>

                    <div className="grid xl:grid-cols-3 grid-cols-1 gap-4">
                        <Select
                            label="Pass Type *"
                            value={formData.passType}
                            disabled={true}
                            onChange={(val) => handleInputChange('passType', val)}
                        >
                            {
                                DAY_PASSES_TYPES_ENUM.map((pair) => (
                                    <Option key={pair.key} value={pair.value}>
                                        <span className='font-bold'>{convertToPresentableText(pair.value)}</span> - <span className='text-gray-500'>{DAY_PASSES_TYPES_DESCRIPTION[pair.key]}</span>
                                    </Option>
                                ))
                            }
                        </Select>

                        <Input
                            label="Visit Date *"
                            type="date"
                            value={formData.visitDate}
                            onChange={(e) => handleInputChange('visitDate', e.target.value)}
                        />

                        <Input
                            label="Purpose"
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                        />
                    </div>

                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                        <Select
                            label="Start Time"
                            value={formData.startTime}
                            onChange={(val) => handleInputChange('startTime', val)}
                        >
                            {HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                <Option key={pair.key} value={pair.key}>{pair.value}</Option>
                            ))}
                        </Select>
                        <Select
                            label="End Time"
                            value={formData.endTime}
                            onChange={(val) => handleInputChange('endTime', val)}
                        >
                            {HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                <Option key={pair.key} value={pair.key}>{pair.value}</Option>
                            ))}
                        </Select>
                        {/* <Input
                            label="Start Time *"
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => handleInputChange('startTime', e.target.value)}
                        />
                        <Input
                            label="End Time *"
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => handleInputChange('endTime', e.target.value)}
                        /> */}
                    </div>
                </div>

                {/* Space Assignment */}
                <div className="space-y-4">
                    <Typography variant="h6" color="blue-gray">Space Assignment (Optional)</Typography>

                    <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
                        <Input
                            label="Seat ID (Optional)"
                            value={formData.seatId}
                            onChange={(e) => handleInputChange('seatId', e.target.value)}
                            disabled={!!formData.officeId}
                        />
                        <Input
                            label="Office ID (Optional)"
                            value={formData.officeId}
                            onChange={(e) => handleInputChange('officeId', e.target.value)}
                            disabled={!!formData.seatId}
                        />
                    </div>

                    <Typography variant="small" className="text-gray-600">
                        Note: Please specify either a Seat ID or Office ID, not both.
                    </Typography>
                </div>

                {/* Additional Requirements */}
                <div className="space-y-4">
                    <Typography variant="h6" color="blue-gray">Additional Requirements</Typography>

                    <Textarea
                        label="Special Requirements"
                        value={formData.specialRequirements}
                        onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                    />

                    <div>
                        <Typography variant="small" className="mb-2 text-gray-700">Equipment Requested:</Typography>
                        <div className="flex flex-wrap gap-2">
                            {['monitor', 'keyboard', 'mouse', 'headphones', 'charger', 'ethernet'].map(equipment => (
                                <Checkbox
                                    key={equipment}
                                    label={equipment.charAt(0).toUpperCase() + equipment.slice(1)}
                                    checked={formData.equipmentRequested.includes(equipment)}
                                    onChange={(e) => handleEquipmentChange(equipment, e.target.checked)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <hr className='my-4' />
                <div className="flex justify-end gap-2">
                    <Button variant="text" color="red" onClick={() => onClose(false)} className="mr-2 rounded-full">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={isSubmitting}
                        className="rounded-full"
                    >
                        Create Day Pass
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default DayPassFormModal;